import {
  Avatar,
  Badge,
  Divider,
  Icon,
  IconNames,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import * as Popover from '@radix-ui/react-popover';
import { useHasAccess } from 'lib-shared-access-control';
import type { LoadDescriptionT, Presence } from 'lib-shared-legacy-types';
import { SearchUserHit, User } from 'lib-shared-types-api';
import capitalize from 'lodash/capitalize';
import moment from 'moment-timezone';
import { ReactNode, useCallback, useMemo, useState } from 'react';

import { presenceApi } from 'apis/presence';
import {
  Presence as ApiPresence,
  PresenceLoadDescription,
} from 'apis/presence/types/presence.types';
import { twilioActions } from 'app/middleware/twilio/actions/twilioActions';
import { presenceActions } from 'components/presence/actions/presenceActions';
import {
  PresenceDropdownItem,
  PresenceToggle,
} from 'components/profile/components/PresenceDropdownItem';
import { userModalActions } from 'components/usersModal';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { getStatus } from '../utils';

import PresenceOverlayChannels from './PresenceOverlayChannels';
import styles from './presenceOverlayPopoverNew.module.scss';

type PresenceOverlayPopoverNewProps = {
  member: SearchUserHit;
  ownProfile: User;
  isFavorite: boolean;
  onAddToFavorites: (userId: string) => void;
  onRemoveFromFavorites: (userId: string) => void;
  children: ReactNode;
};

const hasActivePhoneConversation = (
  loadDescriptions: LoadDescriptionT[],
): boolean =>
  loadDescriptions.some(
    (o) =>
      (o.conversation_channel === 'pstnphone' ||
        o.conversation_channel === 'callback') &&
      o.load_type !== 'acw' &&
      o.load_type !== 'nonlive',
  );

function computeInternalCallState(
  presence: ApiPresence | Presence,
  ownPresence: Presence,
  ownAgentId: string,
  isOwnAgent: boolean,
  canMakeInternalCall: boolean,
) {
  let isConnectedWithOwnAgent = false;
  let isInternalCallCalling = false;
  let internalBtnText = 'Call';

  const internalCalls = presence.presence_status.load_descriptions.filter(
    (o) =>
      o.load_type === 'internalwork' && o.conversation_channel === 'pstnphone',
  );

  const ownInternalCalls = ownPresence.presence_status.load_descriptions.filter(
    (o) =>
      o.load_type === 'internalwork' && o.conversation_channel === 'pstnphone',
  );

  const numberOfAgentsActiveCalls =
    presence.presence_status.load_descriptions.filter(
      (o) =>
        (o.conversation_channel === 'pstnphone' ||
          o.conversation_channel === 'callback') &&
        o.load_type !== 'acw' &&
        o.load_type !== 'nonlive',
    ).length;

  for (const ownInternalCall of ownInternalCalls) {
    for (const internalCall of internalCalls) {
      if (ownInternalCall.csid === internalCall.csid) {
        isConnectedWithOwnAgent = true;
        internalBtnText = 'Connected';

        if (
          ownInternalCall.requester !== null &&
          ownInternalCall.requester.id === ownAgentId
        ) {
          if (internalCall?.conversation?.assignment !== null) {
            // Internal was started by own and accepted - show connected.
          } else {
            // Internal was started by own - show calling at btn
            isInternalCallCalling = true;
            internalBtnText = 'Connected';
          }
          break;
        }
        break;
      }
    }
  }

  let disabledCall = '';
  if (
    canMakeInternalCall === false ||
    isConnectedWithOwnAgent === true ||
    isOwnAgent === true ||
    numberOfAgentsActiveCalls > 0 ||
    presence.status === 'offline' ||
    isInternalCallCalling === true
  ) {
    disabledCall = 'disabled';
  }

  return { internalBtnText, disabledCall };
}

function prepareConversationsByChannel(
  loadDescriptions: readonly PresenceLoadDescription[] | LoadDescriptionT[],
) {
  // Type assertion needed because API returns readonly arrays but PresenceOverlay uses mutable arrays
  const descriptions = loadDescriptions as LoadDescriptionT[];

  const {
    externalPhoneConversations,
    nonelivePhoneConversations,
    chatConversations,
    messengerConversations,
    voicemailConversations,
    emailConversations,
    contactformConversations,
    callbackConversations,
    facebookMessengerConversations,
    acwConversations,
    unknownChannelConversations,
  } = descriptions.reduce<
    Record<
      | 'externalPhoneConversations'
      | 'nonelivePhoneConversations'
      | 'internalPhoneConversations'
      | 'chatConversations'
      | 'messengerConversations'
      | 'voicemailConversations'
      | 'emailConversations'
      | 'contactformConversations'
      | 'callbackConversations'
      | 'facebookMessengerConversations'
      | 'acwConversations'
      | 'unknownChannelConversations',
      LoadDescriptionT[]
    >
  >(
    (acc, description) => {
      if (description.load_type === 'acw') {
        acc.acwConversations.push(description);
        return acc;
      }

      switch (description.conversation_channel) {
        case 'pstnphone': {
          switch (description.load_type) {
            case 'externalwork':
              acc.externalPhoneConversations.push(description);
              break;
            case 'nonlive':
              acc.nonelivePhoneConversations.push(description);
              break;
            case 'internalwork':
              acc.internalPhoneConversations.push(description);
              break;
          }
          break;
        }
        case 'widgetchat':
          acc.chatConversations.push(description);
          break;
        case 'voicemail':
          acc.voicemailConversations.push(description);
          break;
        case 'email':
          acc.emailConversations.push(description);
          break;
        case 'messenger':
          acc.messengerConversations.push(description);
          break;
        case 'facebookmessenger':
          acc.facebookMessengerConversations.push(description);
          break;
        case 'contactform':
          acc.contactformConversations.push(description);
          break;
        case 'callback':
          acc.callbackConversations.push(description);
          break;
        default:
          acc.unknownChannelConversations.push(description);
      }

      return acc;
    },
    {
      externalPhoneConversations: [],
      nonelivePhoneConversations: [],
      internalPhoneConversations: [],
      chatConversations: [],
      messengerConversations: [],
      voicemailConversations: [],
      emailConversations: [],
      contactformConversations: [],
      callbackConversations: [],
      facebookMessengerConversations: [],
      acwConversations: [],
      unknownChannelConversations: [],
    },
  );

  return {
    conversationsByChannel: {
      clock: acwConversations,
      phone: [...externalPhoneConversations, ...nonelivePhoneConversations],
      chat: chatConversations,
      messenger: [...messengerConversations, ...unknownChannelConversations],
      voicemail: voicemailConversations,
      mail: emailConversations,
      'chat-widget': contactformConversations,
      callback: callbackConversations,
      'facebook-messenger': facebookMessengerConversations,
    } as Partial<Record<IconNames, LoadDescriptionT[]>>,
    acwConversations,
  };
}

const presenceBadgeMap: Record<
  Presence['status'],
  'success' | 'warning' | 'secondary'
> = {
  working: 'success',
  away: 'warning',
  offline: 'secondary',
};

export const PresenceOverlayPopoverNew = ({
  member,
  ownProfile,
  isFavorite,
  onAddToFavorites,
  onRemoveFromFavorites,
  children,
}: PresenceOverlayPopoverNewProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [internalBtnText, setInternalBtnText] = useState('Call');

  const isOwnAgent = ownProfile.id === member.id;
  const isOffline = getStatus(member) === 'offline';

  const ownPresence = useAppSelector((state) => state.presence.presence);
  const isSimultaneousRingLoading = useAppSelector(
    (state) => state.presence.meta.toggleSimultaneousRing === 'loading',
  );
  const isMicrophoneBlocked = useAppSelector(
    (state) => state.telephony.isMicrophoneBlocked,
  );

  const featureFlags = useFeatureFlag(['phone-channel']);
  const hasChannelPhone = featureFlags.phoneChannel;

  const hasOrganizationMemberAccess = useHasAccess('organization.member');
  const hasConversationReplyAccess = useHasAccess('conversation.reply');

  const [triggerFetch, { data: presenceData, isLoading, isFetching }] =
    presenceApi.useLazyReadAgentPresenceQuery();

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (open) {
        triggerFetch({ id: member.id });
      }
    },
    [member.id, triggerFetch],
  );

  const canMakeInternalCall = useMemo(() => {
    return (
      !isMicrophoneBlocked &&
      !hasActivePhoneConversation(ownPresence.presence_status.load_descriptions)
    );
  }, [isMicrophoneBlocked, ownPresence]);

  const { disabledCall } = useMemo(() => {
    if (!presenceData?.data?.presence) {
      return {
        internalBtnText: 'Call',
        disabledCall:
          isOffline || isOwnAgent || !canMakeInternalCall ? 'disabled' : '',
      };
    }

    return computeInternalCallState(
      presenceData.data.presence,
      ownPresence,
      ownProfile.id,
      isOwnAgent,
      canMakeInternalCall,
    );
  }, [
    presenceData,
    ownPresence,
    ownProfile.id,
    isOwnAgent,
    canMakeInternalCall,
    isOffline,
  ]);

  const onInternalCallClickHandler = useCallback(() => {
    if (canMakeInternalCall && presenceData?.data?.user) {
      setInternalBtnText('Calling...');
      bindActionToPromise(
        dispatch,
        twilioActions.twilioMakeInternalCall,
      )({
        userId: presenceData.data.user.id,
      });
    }
  }, [canMakeInternalCall, presenceData?.data?.user, dispatch]);

  const handlePresenceLinkClick = useCallback(
    (csid: LoadDescriptionT['csid']) => {
      navigate({
        path: '/conversation/:csid',
        params: { path: { csid: `${csid}` } },
      });
    },
    [navigate],
  );

  const presenceSetOthersSimultaneousRinging = useCallback(
    (payload: { userId: string; enabled: boolean }) => {
      return bindActionToPromise(
        dispatch,
        presenceActions.presenceSetOthersSimultaneousRinging,
      )(payload);
    },
    [dispatch],
  );

  const showUserModal = useCallback(
    (payload: { user: User }) => {
      dispatch(userModalActions.show(payload));
    },
    [dispatch],
  );

  const {
    conversationsByChannel,
    lastSeen,
    acwConversationNode,
    hasPhone,
    presence,
    user,
  } = useMemo(() => {
    if (!presenceData?.data) {
      return {
        conversationsByChannel: {} as Partial<
          Record<IconNames, LoadDescriptionT[]>
        >,
        lastSeen: null,
        acwConversationNode: null,
        hasPhone: false,
        presence: null,
        user: null,
      };
    }

    const { presence, user } = presenceData.data;
    const { conversationsByChannel, acwConversations } =
      prepareConversationsByChannel(presence.presence_status.load_descriptions);

    const numberOfAcw = acwConversations.length;
    let lastSeen: string | null = null;
    let acwConversationNode: JSX.Element | null = null;

    if (numberOfAcw > 0) {
      const conversation = numberOfAcw === 1 ? 'conversation' : 'conversations';
      acwConversationNode = (
        <span>
          Wrap-up time on {numberOfAcw} {conversation}
        </span>
      );
    }

    if (presence.status === 'offline') {
      lastSeen =
        presence.last_seen === null
          ? 'Has not signed-in yet'
          : `Last seen ${moment(presence.last_seen).fromNow()}`;
    }

    const hasPhone = !!user.phone_number && user.phone_number.length > 0;

    return {
      conversationsByChannel,
      lastSeen,
      acwConversationNode,
      hasPhone,
      presence,
      user,
    };
  }, [presenceData]);

  const hasConversationsAssigned = Object.keys(conversationsByChannel).some(
    (channel) =>
      (conversationsByChannel as Record<string, LoadDescriptionT[]>)[channel]
        ?.length > 0,
  );

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>

      <Popover.Portal>
        <Popover.Content className={styles.dropdown} side="right">
          {isLoading || isFetching ? (
            <div className={styles.loadingContainer}>
              <Spinner size={24} />
            </div>
          ) : presence && user ? (
            <>
              <div className={styles.header}>
                <Avatar user={user} />
                <Text.Paragraph className={styles.name} as="span" variant="2">
                  {user.name !== undefined && user.name !== null
                    ? user.name.replace(/ .*/, '')
                    : ''}
                </Text.Paragraph>
                {presence.status && (
                  <Badge
                    variant={presenceBadgeMap[presence.status] ?? 'secondary'}
                    size="medium"
                  >
                    {capitalize(presence.status)}
                  </Badge>
                )}
              </div>

              {/* Type assertion needed: API Presence type is structurally compatible with legacy Presence */}
              <PresenceOverlayChannels presence={presence as Presence} />

              <Divider />

              <div>
                {[acwConversationNode, lastSeen]
                  .filter(Boolean)
                  .map((item, index) => (
                    <div key={index}>
                      <Text.Paragraph
                        variant="2"
                        as="div"
                        color="tertiary"
                        padding="200 0"
                        align="center"
                      >
                        {item}
                      </Text.Paragraph>
                      <Divider />
                    </div>
                  ))}

                {hasChannelPhone && (
                  <>
                    {hasConversationReplyAccess &&
                      hasOrganizationMemberAccess && (
                        <PresenceDropdownItem
                          icon="simultaneous"
                          text="Simultaneous ring"
                          tooltipText={
                            hasPhone
                              ? `Toggle simultaneous ringing for ${
                                  user && user.name ? user.name : 'agent'
                                }`
                              : 'Simultaneous ringing is turned off (phone number needed)'
                          }
                          disabled={!hasPhone}
                        >
                          <PresenceToggle
                            id="shouldOpenSimultaneous"
                            disabled={hasPhone && isSimultaneousRingLoading}
                            active={
                              !!presence.presence_status.simultaneous_ringing
                            }
                            onToggle={
                              hasPhone && !isSimultaneousRingLoading
                                ? () => {
                                    presenceSetOthersSimultaneousRinging({
                                      userId: user.id,
                                      enabled:
                                        !presence.presence_status
                                          .simultaneous_ringing,
                                    });
                                  }
                                : () => {}
                            }
                          />
                        </PresenceDropdownItem>
                      )}

                    {hasConversationReplyAccess && !isOwnAgent && (
                      <PresenceDropdownItem
                        icon="phone"
                        text={internalBtnText}
                        tooltipText={`Make internal call to ${user.name}`}
                        disabled={disabledCall !== ''}
                        onClick={onInternalCallClickHandler}
                      />
                    )}
                  </>
                )}

                {(isOwnAgent || hasOrganizationMemberAccess) && (
                  <PresenceDropdownItem
                    icon="pen"
                    text="Edit agent"
                    onClick={() => {
                      showUserModal({ user });
                    }}
                  />
                )}

                <PresenceDropdownItem
                  icon={
                    <Icon
                      name={isFavorite ? 'star-full' : 'star'}
                      color={isFavorite ? 'orange.300' : 'neutral.400'}
                    />
                  }
                  text={isFavorite ? 'Unfavorite agent' : 'Favorite agent'}
                  onClick={() =>
                    isFavorite
                      ? onRemoveFromFavorites(user.id)
                      : onAddToFavorites(user.id)
                  }
                />

                <Divider />

                {!hasConversationsAssigned ? (
                  <PresenceDropdownItem
                    icon="chat-slash"
                    text="No conversations here"
                    disabled={true}
                  />
                ) : (
                  (Object.keys(conversationsByChannel) as IconNames[]).map(
                    (iconName) => {
                      return conversationsByChannel[iconName]?.map(
                        (item, index) => (
                          <PresenceDropdownItem
                            key={index}
                            icon={iconName}
                            text={`Open #${item.csid}`}
                            onClick={() => handlePresenceLinkClick(item.csid)}
                          />
                        ),
                      );
                    },
                  )
                )}
              </div>
            </>
          ) : null}
          <Popover.Arrow
            style={{ fill: colors.neutral['000'] }}
            width={16}
            height={8}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
